import React, { useState } from 'react';
import { ApiClientError } from '../../api/http';
import {
  PRODUCT_AREAS,
  SCREENSHOT_MAX_BYTES,
  SCREENSHOT_TYPES,
  productAreaFromRoute,
  safeRoute,
  submitProblemReport,
} from '../../api/supportReports';
import { recentActivity } from '../../lib/recentActivity';

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('the screenshot could not be read'));
    reader.onload = () => {
      const value = String(reader.result || '');
      resolve(value.slice(value.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

/** Describes why a screenshot is refused, or `''` when the browser-side limits pass. */
export function screenshotProblem(file: { type: string; size: number } | null): string {
  if (!file) return '';
  if (!(SCREENSHOT_TYPES as readonly string[]).includes(file.type)) return 'Use a PNG, JPEG or WebP image.';
  if (file.size > SCREENSHOT_MAX_BYTES) return 'The screenshot must be 1 MB or smaller.';
  return '';
}

export function ReportProblemForm({ sourcePath = '' }: { sourcePath?: string }) {
  const route = safeRoute(sourcePath);
  const [area, setArea] = useState(productAreaFromRoute(sourcePath));
  const [happened, setHappened] = useState('');
  const [expected, setExpected] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [includeActivity, setIncludeActivity] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState('');

  const fileProblem = screenshotProblem(file);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    if (!happened.trim()) {
      setError('Tell us what happened.');
      return;
    }
    if (fileProblem) {
      setError(fileProblem);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const screenshot = file ? { type: file.type, data: await readAsBase64(file) } : undefined;
      const result = await submitProblemReport({
        what_happened: happened.trim(),
        what_expected: expected.trim(),
        product_area: area,
        route,
        ...(screenshot ? { screenshot } : {}),
        include_activity: includeActivity,
        ...(includeActivity ? { activity: recentActivity() } : {}),
      });
      setReceipt(result.receipt_ref);
    } catch (failure) {
      setError(
        failure instanceof ApiClientError && failure.status === 429
          ? 'You have sent several reports recently. Please try again later.'
          : failure instanceof ApiClientError && failure.status === 401
            ? 'Sign in to send a report, or email support.'
            : 'Your report could not be sent. Please try again, or email support.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (receipt) {
    return (
      <div className="notice report-problem-done" role="status">
        <h2>Thank you</h2>
        <p>Report #{receipt} received. You can follow its status on this page.</p>
      </div>
    );
  }

  return (
    <form className="report-problem" aria-label="Report a problem" onSubmit={submit}>
      <h2 id="report-problem-heading">Report a problem</h2>
      <label htmlFor="report-happened">What happened?</label>
      <textarea id="report-happened" name="what_happened" rows={4} maxLength={4000} required
        value={happened} onChange={(e) => setHappened(e.target.value)} />
      <label htmlFor="report-expected">What did you expect?</label>
      <textarea id="report-expected" name="what_expected" rows={3} maxLength={4000}
        value={expected} onChange={(e) => setExpected(e.target.value)} />
      <label htmlFor="report-area">Product area</label>
      <select id="report-area" name="product_area" value={area} onChange={(e) => setArea(e.target.value)}>
        {PRODUCT_AREAS.map((item) => <option key={item} value={item}>{item}</option>)}
      </select>
      <label htmlFor="report-screenshot">Screenshot (optional, PNG, JPEG or WebP, up to 1 MB)</label>
      <input id="report-screenshot" name="screenshot" type="file" accept={SCREENSHOT_TYPES.join(',')}
        onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      {fileProblem ? <p role="alert">{fileProblem}</p> : null}
      <label className="checkbox-row" htmlFor="report-activity">
        <input id="report-activity" name="include_activity" type="checkbox" checked={includeActivity}
          onChange={(e) => setIncludeActivity(e.target.checked)} />
        Include my recent activity (request ids and error codes only; no project content or secrets)
      </label>
      {error ? <p role="alert">{error}</p> : null}
      <button type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send report'}</button>
    </form>
  );
}
